import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import Dashboard from '../pages/dashboard';
import NotFound from '../pages/404';
import SignIn from '../pages/signin';
import Account from '../pages/account';
import RequireAuth from './RequireAuth';

/**
 * A component that throws, reachable only in development.
 *
 * The error boundary is the one piece of the application whose value is
 * entirely in what happens when something else fails, and the only honest way
 * to test that is to make something fail. `import.meta.env.DEV` is replaced
 * with `false` at build time, so this route and this component are removed
 * from the production bundle by dead-code elimination.
 */
const DeliberateCrash = () => {
  throw new Error('Deliberate crash, for the error-boundary test');
};

// Staff screens
import DashboardView from '../views/dashboard';
import LearningHome from '../views/learning/home';
import SubjectTopics from '../views/learning/topics';
import NewTopic from '../views/learning/newTopic';
import EditTopic from '../views/learning/editTopic';
import ActivityHome from '../views/activity/home';
import ActivitySubmissions from '../views/activity/submission';
import SupportView from '../views/support';
import MessagingHome from '../views/messaging/home';
import CreateMessage from '../views/messaging/create';
import ProfileView from '../views/profile';
import UserHome from '../views/user/userHome';
import AddNewUser from '../views/user/addNew';
import UserDetail from '../views/user/userDetail';
import CohortHome from '../views/cohort/home';
import CohortDetail from '../views/cohort/detail';

// Learner screens
import StudentLanding from '../views/studentViews/home/landing';
import StudentSubjects from '../views/studentViews/home/subjects';
import StudentLearn from '../views/studentViews/home/learn';
import StudentActivityHome from '../views/studentViews/studentActivity/home';
import StudentActivityDetail from '../views/studentViews/studentActivity/detail';
import StudentSupportHome from '../views/studentViews/studentSupport/home';
import StudentSupportReply from '../views/studentViews/studentSupport/reply';
import StudentProfile from '../views/studentViews/profile';

import { ACCOUNT, DASHBOARD, LOGIN } from './paths';

/**
 * The whole route table, in one place.
 *
 * It used to be spread over nine files: a top-level Switch, a per-role table
 * that also named the component to render, and a `routes.js` beside each view
 * with its own nested Switch. Three levels of nesting made it hard to answer
 * "what does this URL render", and the menu and the routes were the same
 * list, so a screen with no sidebar entry needed a hidden one.
 *
 * Paths below the dashboard are relative to it, and ranked by specificity
 * rather than by declaration order, so `learning/topic/new` wins over
 * `learning/:subjectId` wherever it appears.
 */
const AppRoutes = () => (
  <Routes>
    <Route path={LOGIN} element={<SignIn />} />
    <Route path={ACCOUNT} element={<Account />} />

    <Route element={<RequireAuth />}>
      <Route path={DASHBOARD} element={<Dashboard />}>
        <Route index element={<DashboardView />} />

        <Route path="learning" element={<LearningHome />} />
        <Route path="learning/topic/new" element={<NewTopic />} />
        <Route path="learning/topic/edit" element={<EditTopic />} />
        <Route path="learning/:subjectId" element={<SubjectTopics />} />

        <Route path="activities" element={<ActivityHome />} />
        <Route path="activities/:activityId" element={<ActivitySubmissions />} />

        <Route path="support" element={<SupportView />} />

        <Route path="messaging/sms" element={<MessagingHome />} />
        <Route path="messaging/sms/new" element={<CreateMessage />} />
        <Route path="messaging/email" element={<MessagingHome />} />
        <Route path="messaging/email/new" element={<CreateMessage />} />

        <Route path="profile" element={<ProfileView />} />

        <Route path="setup/users" element={<UserHome />} />
        <Route path="setup/users/new/:userType" element={<AddNewUser />} />
        <Route path="setup/users/detail/:id" element={<UserDetail />} />
        <Route path="setup/cohort" element={<CohortHome />} />
        <Route path="setup/cohort/:id" element={<CohortDetail />} />

        <Route path="student/home" element={<StudentLanding />} />
        <Route path="student/home/cohort/:id" element={<StudentSubjects />} />
        <Route path="student/home/cohort/:id/learn" element={<StudentLearn />} />
        <Route path="student/activities" element={<StudentActivityHome />} />
        <Route path="student/activities/:id" element={<StudentActivityDetail />} />
        <Route path="student/support" element={<StudentSupportHome />} />
        <Route path="student/support/:id" element={<StudentSupportReply />} />
        <Route path="student/profile" element={<StudentProfile />} />

        <Route path="*" element={<Navigate to={DASHBOARD} replace />} />
      </Route>
    </Route>

    {import.meta.env.DEV && <Route path="/__crash" element={<DeliberateCrash />} />}

    <Route path="*" element={<NotFound />} />
  </Routes>
);

export default AppRoutes;
